import { ShapeType } from '../types/commontypes';
import type { ShapePropertyInputDef } from '../types/commontypes';
import { ShapePropertyInputType } from '../types/commontypes';
import { GANN_FAN_LABELS } from '@/types/shape';

// ── Shared property group constants ──
export const SharedPropertyGroup = {
  EXTENSIBLE_INFO_LINE: 'ExtensibleInfoLine',
  VOLUME_PROFILE: 'VolumeProfile',
} as const;

// ── ShapeType → property definition key mapping ──
export const ShapePropertyDefKey: Record<ShapeType, string> = {
  [ShapeType.TREND_LINE]: SharedPropertyGroup.EXTENSIBLE_INFO_LINE,
  [ShapeType.RAY]: SharedPropertyGroup.EXTENSIBLE_INFO_LINE,
  [ShapeType.INFO_LINE]: SharedPropertyGroup.EXTENSIBLE_INFO_LINE,
  [ShapeType.FIXED_RANGE_VOLUME_PROFILE]: SharedPropertyGroup.VOLUME_PROFILE,
  [ShapeType.ANCHORED_VOLUME_PROFILE]: SharedPropertyGroup.VOLUME_PROFILE,
  [ShapeType.MEASURE]: ShapeType.MEASURE,
  [ShapeType.HORZ_LINE]: ShapeType.HORZ_LINE,
  [ShapeType.VERT_LINE]: ShapeType.VERT_LINE,
  [ShapeType.PARALLEL_CHANNEL]: ShapeType.PARALLEL_CHANNEL,
  [ShapeType.FIB_RETRACEMENT]: ShapeType.FIB_RETRACEMENT,
  [ShapeType.FIB_CHANNEL]: ShapeType.FIB_CHANNEL,
  [ShapeType.GANN_BOX]: ShapeType.GANN_BOX,
  [ShapeType.GANN_FAN]: ShapeType.GANN_FAN,
  [ShapeType.ELLIOTT_IMPULSE_12345]: ShapeType.ELLIOTT_IMPULSE_12345,
  [ShapeType.ELLIOTT_CORRECTION_ABC]: ShapeType.ELLIOTT_CORRECTION_ABC,
  [ShapeType.XABCD_PATTERN]: ShapeType.XABCD_PATTERN,
  [ShapeType.RECTANGLE]: ShapeType.RECTANGLE,
  [ShapeType.TRIANGLE]: ShapeType.TRIANGLE,
  [ShapeType.PATH]: ShapeType.PATH,
  [ShapeType.TEXT]: ShapeType.TEXT,
  [ShapeType.ORDER_LINE]: ShapeType.ORDER_LINE,
};

// ── Unified shape property definitions ──
export const ShapeProperties: Record<string, ShapePropertyInputDef[]> = {
  [SharedPropertyGroup.EXTENSIBLE_INFO_LINE]: [
    {
      name: 'line',
      type: ShapePropertyInputType.LINE_OPTION,
      label: 'Line',
      showInMiniToolbar: true,
    },
    {
      name: 'arrow',
      type: ShapePropertyInputType.ARROW_OPTION,
      label: 'Arrow',
    },
    {
      name: 'extend',
      type: ShapePropertyInputType.EXTEND_OPTION,
      label: 'Extend',
    },
    {
      name: 'stats',
      type: ShapePropertyInputType.SEPARATOR,
      label: 'STATS',
    },
    {
      name: 'showPriceRange',
      type: ShapePropertyInputType.CHECKBOX,
      label: 'Price range',
    },
    {
      name: 'showPercentChange',
      type: ShapePropertyInputType.CHECKBOX,
      label: 'Percent change',
    },
    {
      name: 'showBarsRange',
      type: ShapePropertyInputType.CHECKBOX,
      label: 'Bars range',
    },
    {
      name: 'showDateTimeRange',
      type: ShapePropertyInputType.CHECKBOX,
      label: 'Date/time range',
    },
    {
      name: 'showAngle',
      type: ShapePropertyInputType.CHECKBOX,
      label: 'Angle',
    },
    {
      name: 'alwaysShow',
      type: ShapePropertyInputType.CHECKBOX,
      label: 'Always show',
    },
  ],
  [ShapeType.ELLIOTT_IMPULSE_12345]: [
    {
      name: 'line',
      type: ShapePropertyInputType.LINE_OPTION,
      label: 'Line',
      showInMiniToolbar: true,
    },
  ],
  [ShapeType.FIB_RETRACEMENT]: [
    {
      name: 'trendLine',
      type: ShapePropertyInputType.LINE_OPTION,
      label: 'Trend line',
    },
    {
      name: 'levelsLine',
      type: ShapePropertyInputType.LINE_WIDTH_DASH_OPTION,
      label: 'Levels line',
    },
    {
      name: 'extend',
      type: ShapePropertyInputType.EXTEND_OPTION,
      label: 'Extend',
    },
    {
      name: 'levels',
      type: ShapePropertyInputType.SHOW_VALUE_COLOR_OPTIONS,
      label: '',
    },
    {
      name: 'background',
      type: ShapePropertyInputType.SHOW_OPACITY_OPTION,
      label: 'Background',
    },
  ],
  [ShapeType.FIB_CHANNEL]: [
    {
      name: 'levelsLine',
      type: ShapePropertyInputType.LINE_WIDTH_DASH_OPTION,
      label: 'Levels line',
    },
    {
      name: 'extend',
      type: ShapePropertyInputType.EXTEND_OPTION,
      label: 'Extend',
    },
    {
      name: 'levels',
      type: ShapePropertyInputType.SHOW_VALUE_COLOR_OPTIONS,
      label: '',
    },
    {
      name: 'background',
      type: ShapePropertyInputType.SHOW_OPACITY_OPTION,
      label: 'Background',
    },
  ],
  [ShapeType.GANN_BOX]: [
    {
      name: 'price',
      type: ShapePropertyInputType.SEPARATOR,
      label: 'Price levels',
    },
    {
      name: 'priceLevels',
      type: ShapePropertyInputType.SHOW_VALUE_COLOR_OPTIONS,
      label: 'Price levels',
    },
    {
      name: 'priceBackground',
      type: ShapePropertyInputType.SHOW_OPACITY_OPTION,
      label: 'Background',
    },
    {
      name: 'time',
      type: ShapePropertyInputType.SEPARATOR,
      label: 'Time levels',
    },
    {
      name: 'timeLevels',
      type: ShapePropertyInputType.SHOW_VALUE_COLOR_OPTIONS,
      label: 'Time levels',
    },
    {
      name: 'timeBackground',
      type: ShapePropertyInputType.SHOW_OPACITY_OPTION,
      label: 'Background',
    },
  ],
  [ShapeType.GANN_FAN]: [
    {
      name: 'levels',
      type: ShapePropertyInputType.SHOW_COLOR_OPTIONS,
      label: GANN_FAN_LABELS.join(','),
    },
    {
      name: 'background',
      type: ShapePropertyInputType.SHOW_OPACITY_OPTION,
      label: 'Background',
    },
  ],
  [ShapeType.TEXT]: [
    {
      name: 'text',
      type: ShapePropertyInputType.TEXT,
      label: 'Text',
    },
    {
      name: 'font',
      type: ShapePropertyInputType.FONT_OPTION,
      label: '',
      showInMiniToolbar: true,
    },
    {
      name: 'background',
      type: ShapePropertyInputType.SHOW_COLOR_OPTIONS,
      label: 'Background',
      showInMiniToolbar: true,
    },
  ],
  [ShapeType.HORZ_LINE]: [
    {
      name: 'line',
      type: ShapePropertyInputType.LINE_OPTION,
      label: 'Line',
      showInMiniToolbar: true,
    },
  ],
  [ShapeType.VERT_LINE]: [
    {
      name: 'line',
      type: ShapePropertyInputType.LINE_OPTION,
      label: 'Line',
      showInMiniToolbar: true,
    },
  ],
  [ShapeType.PARALLEL_CHANNEL]: [
    {
      name: 'channel',
      type: ShapePropertyInputType.LINE_OPTION,
      label: 'Channel',
      showInMiniToolbar: true,
    },
    {
      name: 'middle',
      type: ShapePropertyInputType.SHOW_LINE_OPTION,
      label: 'Middle',
    },
    {
      name: 'extend',
      type: ShapePropertyInputType.EXTEND_OPTION,
      label: 'Extend',
    },
    {
      name: 'background',
      type: ShapePropertyInputType.SHOW_COLOR_OPTIONS,
      label: 'Background',
      showInMiniToolbar: true,
    },
  ],
  [ShapeType.RECTANGLE]: [
    {
      name: 'border',
      type: ShapePropertyInputType.LINE_OPTION,
      label: 'Border',
      showInMiniToolbar: true,
    },
    {
      name: 'middle',
      type: ShapePropertyInputType.SHOW_LINE_OPTION,
      label: 'Middle line',
    },
    {
      name: 'background',
      type: ShapePropertyInputType.SHOW_COLOR_OPTIONS,
      label: 'Background',
      showInMiniToolbar: true,
    },
    {
      name: 'extend',
      type: ShapePropertyInputType.EXTEND_OPTION,
      label: 'Extend',
    },
  ],
  [ShapeType.TRIANGLE]: [
    {
      name: 'border',
      type: ShapePropertyInputType.LINE_OPTION,
      label: 'Border',
      showInMiniToolbar: true,
    },
    {
      name: 'background',
      type: ShapePropertyInputType.SHOW_COLOR_OPTIONS,
      label: 'Background',
      showInMiniToolbar: true,
    },
  ],
  [ShapeType.PATH]: [
    {
      name: 'line',
      type: ShapePropertyInputType.LINE_OPTION,
      label: 'Line',
      showInMiniToolbar: true,
    },
    {
      name: 'arrow',
      type: ShapePropertyInputType.ARROW_OPTION,
      label: 'Arrow',
    },
  ],
  [ShapeType.ELLIOTT_CORRECTION_ABC]: [
    {
      name: 'line',
      type: ShapePropertyInputType.LINE_OPTION,
      label: 'Line',
      showInMiniToolbar: true,
    },
  ],
  [ShapeType.XABCD_PATTERN]: [
    {
      name: 'label',
      type: ShapePropertyInputType.FONT_BASIC_OPTION,
      label: 'Label',
      showInMiniToolbar: true,
    },
    {
      name: 'border',
      type: ShapePropertyInputType.LINE_OPTION,
      label: 'Border',
      showInMiniToolbar: true,
    },
    {
      name: 'background',
      type: ShapePropertyInputType.SHOW_COLOR_OPTIONS,
      label: 'Background',
      showInMiniToolbar: true,
    },
  ],
  [SharedPropertyGroup.VOLUME_PROFILE]: [
    // Inputs
    { name: 'inputs', type: ShapePropertyInputType.SEPARATOR, label: 'INPUTS' },
    { name: 'rowSize', type: ShapePropertyInputType.NUMBER, label: 'Row Size', min: 1, max: 200, step: 1 },
    {
      name: 'valueAreaVolume',
      type: ShapePropertyInputType.NUMBER,
      label: 'Value Area Volume',
      min: 50,
      max: 100,
      step: 1,
    },

    // Style
    { name: 'style', type: ShapePropertyInputType.SEPARATOR, label: 'STYLE' },
    { name: 'values', type: ShapePropertyInputType.SHOW_COLOR_OPTION, label: 'Values' },
    { name: 'width', type: ShapePropertyInputType.NUMBER, label: 'Width (%)', min: 10, max: 100, step: 10 },
    { name: 'placement', type: ShapePropertyInputType.SELECT, label: 'Placement', options: ['Left', 'Right'] },
    { name: 'upVolume', type: ShapePropertyInputType.COLOR_PICKER, label: 'Up Volume', disabledAlpha: true },
    { name: 'downVolume', type: ShapePropertyInputType.COLOR_PICKER, label: 'Down Volume', disabledAlpha: true },
    { name: 'vah', type: ShapePropertyInputType.SHOW_LINE_OPTION, label: 'VAH' },
    { name: 'val', type: ShapePropertyInputType.SHOW_LINE_OPTION, label: 'VAL' },
    { name: 'poc', type: ShapePropertyInputType.SHOW_LINE_OPTION, label: 'POC' },
    { name: 'histogramBox', type: ShapePropertyInputType.COLOR_PICKER, label: 'Background' },
  ],
};
